<template>
    <div class="md-layout" v-if="hasTeam">
        <div class="md-layout-item md-medium-size-100 md-xsmall-size-100 md-size-100">
            <div class="custom-toolbar">
                <div class="custom-toolbar-start">
                    <router-link :to="{ name: 'Team Settings' }">
                        <h3 class="title">Team Settings</h3>
                    </router-link>
                    <h3 class="title">&nbsp;&nbsp; > &nbsp;&nbsp; {{ $t('menus.setting.tags') }}</h3>
                </div>
            </div>
        </div>
        <div class="md-layout-item md-size-100">
            <md-card>
                <md-card-header class="md-card-header-icon md-card-header-blue">
                    <div class="card-icon">
                        <md-icon>tag</md-icon>
                    </div>
                </md-card-header>
                <md-card-content>
                    <div class="cf-container">
                        <div class="cf-action-container">
                            <md-switch class="custom-switch" v-model="showDeletedTags">Deleted tags</md-switch>
                            <md-button
                                title="Add Tag"
                                class="md-primary md-just-icon md-round"
                                @click.stop="handleAddTag()"
                            >
                                <md-icon>add</md-icon>
                            </md-button>
                        </div>
                        <md-table class="custom-paginated-table">
                            <md-table-row>
                                <md-table-head>Name</md-table-head>
                                <md-table-head>In Use</md-table-head>
                                <md-table-head>Actions</md-table-head>
                            </md-table-row>

                            <md-table-row v-for="(tag, index) in tagList" :key="index">
                                <md-table-cell>
                                    {{ tag.name }}
                                </md-table-cell>
                                <md-table-cell>
                                    {{ tag.totalTagCount }}
                                    <md-tooltip
                                        class="tooltip-container"
                                        v-if="
                                            tag.customerTagCount > 0 ||
                                                tag.teamMemberTagCount > 0 ||
                                                tag.adjustmentTagCount > 0
                                        "
                                        md-direction="left"
                                    >
                                        <span v-if="tag.customerTagCount > 0">
                                            Customers: {{ tag.customerTagCount }}
                                        </span>
                                        <span v-if="tag.teamMemberTagCount > 0">
                                            Team members: {{ tag.teamMemberTagCount }}
                                        </span>
                                        <span v-if="tag.adjustmentTagCount > 0">
                                            Adjustments: {{ tag.adjustmentTagCount }}
                                        </span>
                                    </md-tooltip>
                                </md-table-cell>
                                <md-table-cell class="action-buttons">
                                    <md-button
                                        v-if="tag.isDeleted"
                                        title="enable tag"
                                        class="md-success md-just-icon md-round"
                                        @click.stop="enableTag(tag)"
                                    >
                                        <md-icon>add</md-icon>
                                    </md-button>

                                    <md-button
                                        v-if="!tag.isDeleted"
                                        title="delete tag"
                                        class="md-danger md-just-icon md-round"
                                        @click.stop="deleteTag(tag)"
                                    >
                                        <md-icon>delete</md-icon>
                                    </md-button>

                                    <md-button
                                        v-if="!tag.isDeleted"
                                        title="edit tag"
                                        class="md-warning md-just-icon md-round"
                                        @click.stop="editTag(tag)"
                                    >
                                        <md-icon>edit</md-icon>
                                    </md-button>
                                </md-table-cell>
                            </md-table-row>
                        </md-table>
                        <div class="text-center" v-if="tagList.length == 0">
                            <p class="no-result-message">No tags created.</p>
                        </div>
                    </div>
                </md-card-content>
            </md-card>
        </div>
    </div>
</template>

<script>
import { handleRequests, showErrorMessage } from '@/helpers';
import { GeneralMixin } from '@/mixins/GeneralMixin';
import { mapGetters } from 'vuex';
import CreateEditTagModal from './Tags/CreateEditTagModal';

export default {
    name: 'Tags',
    mixins: [GeneralMixin],
    components: {},
    data() {
        return {
            tagList: [],
            showDeletedTags: false
        };
    },
    watch: {
        async showDeletedTags() {
            await this.getTags();
        }
    },
    computed: {
        ...mapGetters({
            hasTeam: 'user/hasTeam'
        })
    },
    async mounted() {
        await this.getTags();
    },
    methods: {
        async getTags() {
            this.$_handleLoaderState(true);
            try {
                const api = `/api/teams/settings/tags?showDeletedTags=${this.showDeletedTags}`;
                const response = await handleRequests(api);

                if (response.data !== null) {
                    this.tagList = response.data;
                }
            } catch (error) {
                const message = 'Error in getting tags.';
                showErrorMessage(this, message, null);
            } finally {
                this.$_handleLoaderState(false);
            }
        },
        async handleAddTag() {
            this.$modal.show(CreateEditTagModal).then((response) => {
                if (response !== null) 
                    this.tagList.push(response);
            });
        },
        async deleteTag(tag) {
            const customerMessage = tag.customerTagCount === 1 ? `Customer` : `Customers`;
            const teamMemberMessage = tag.teamMemberTagCount === 1 ? `Team member` : `Team members`;
            const adjustmentMessages = tag.adjustmentTagCount === 1 ? `Adjustment` : `Adjustments`;

            const messages = [];

            if (tag.customerTagCount > 0) {
                messages.push(`${tag.customerTagCount} ${customerMessage}`);
            }

            if (tag.teamMemberTagCount > 0) {
                messages.push(`${tag.teamMemberTagCount} ${teamMemberMessage}`);
            }

            if (tag.adjustmentTagCount > 0) {
                messages.push(`${tag.adjustmentTagCount} ${adjustmentMessages}`);
            }

            const messageBody =
                messages.length > 0
                    ? `Are you sure you want to delete this tag ${
                        tag.name
                    }? <br /> This will be removed from ${messages.join(', ')}`
                    : `Are you sure you want to delete this tag ${tag.name}?`;

            this.$messageBox
                .show({
                    title: `Delete ${tag.name}`,
                    body: messageBody,
                    buttons: ['Confirm', 'Cancel']
                })
                .then(async (response) => {
                    if (response.toLowerCase() === 'confirm') {
                        this.$_handleLoaderState(true, 'DELETING...');
                        const payload = {
                            method: 'delete'
                        };

                        const api = `/api/teams/settings/tag/${tag.tagId}`;
                        try {
                            await handleRequests(api, payload);
                            this.$notify({
                                message: 'Tag deleted!',
                                type: 'success'
                            });

                            this.tagList = this.tagList.filter((x) => x.tagId !== tag.tagId);

                            this.$_handleLoaderState(false);
                        } catch (e) {
                            const message = 'Cannot delete tag.';
                            showErrorMessage(this, message, null);
                            this.$_handleLoaderState(false);
                        }
                    }
                });
        },
        async editTag(tagData) {
            this.$modal.show(CreateEditTagModal, { tag: tagData }).then((response) => {
                this.tagList.find((x) => x.tagId === response.tagId).name = response.name;
            });
        },
        async enableTag(tag) {
            const api = `/api/teams/settings/tag/enable/${tag.tagId}`;
            const payload = {
                method: 'post'
            };

            this.$_handleLoaderState(true, 'ENABLING...');

            try {
                await handleRequests(api, payload);
                this.$notify({
                    message: 'Tag enabled!',
                    type: 'success'
                });

                this.tagList = this.tagList.filter((x) => x.tagId !== tag.tagId);
            } catch (e) {
                const message = 'Cannot enable tag.';
                showErrorMessage(this, message, null);
            } finally {
                this.$_handleLoaderState(false);
            }
        }
    }
};
</script>
<style lang="scss" scoped>
.actions {
    width: 6%;
}

.custom-paginated-table .action-buttons {
    width: 100px;
}

.card-icon {
    background: #a745c7 !important;
}

.custom-switch {
    position: absolute;
    right: 30px;
    top: -10px;
    min-width: 130px;

    .md-switch {
        margin: 0;
        margin-bottom: 10px;
    }
}

.tooltip-container {
    span {
        display: block;
        margin-bottom: 4px;

        &:last-child {
            margin-bottom: 0;
        }
    }
}
</style>
